import { useState, useEffect, useCallback } from "react";
import axios from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { QRCodeSVG } from "qrcode.react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  X, Star, Mail, Phone, Package, MapPin, Search, Plus, Pencil, Trash2,
  RefreshCw, QrCode, IdCard, Eye, BadgeCheck, UserCheck, UserX, ShieldOff,
  MessageSquare, Send, Bike, Car, Truck, ShieldCheck, Building2,
} from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";
import { COUNTRY_CODES } from "../../utils/phoneCountryCodes";
import UserVerificationModal from "../../components/modals/UserVerificationModal";

dayjs.locale("es");

const STATUS_LABEL = { disponible: "Disponible", ocupado: "Ocupado", offline: "Offline" };
const STATUS_STYLE = {
  disponible: "bg-green-100 text-green-700",
  ocupado: "bg-amber-100 text-amber-700",
  offline: "bg-gray-100 text-gray-600",
};
const APPROVAL_LABEL = { aprobado: "Aprobado", pendiente: "Pendiente", rechazado: "Rechazado" };
const APPROVAL_STYLE = {
  aprobado: "bg-green-100 text-green-700",
  pendiente: "bg-amber-100 text-amber-700",
  rechazado: "bg-red-100 text-red-600",
};
const DOC_STYLE = {
  aprobado: "bg-blue-50 text-blue-700 border border-blue-200",
  pendiente: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  rechazado: "bg-red-50 text-red-600 border border-red-200",
};
const DOC_LABEL = { aprobado: "Verificado", pendiente: "En revisión", rechazado: "Rechazado" };
const VEHICLE_OPTIONS = [
  { value: "moto", label: "Moto" },
  { value: "bicicleta", label: "Bicicleta" },
  { value: "carro", label: "Carro" },
  { value: "camion", label: "Camión" },
  { value: "otro", label: "Otro" },
];
const VEHICLE_ICON = { moto: Bike, bicicleta: Bike, carro: Car, camion: Truck, otro: Truck };
const DOC_TYPES = [
  { value: "CC", label: "Cédula de ciudadanía" },
  { value: "CE", label: "Cédula de extranjería" },
  { value: "TI", label: "Tarjeta de identidad" },
  { value: "PASAPORTE", label: "Pasaporte" },
];
const ORDER_STATUS_STYLE = {
  entregado: "bg-green-100 text-green-700",
  cancelado: "bg-red-100 text-red-600",
  en_camino: "bg-amber-100 text-amber-700",
  pendiente: "bg-yellow-100 text-yellow-700",
  confirmado: "bg-blue-100 text-blue-700",
  asignado: "bg-violet-100 text-violet-700",
};

const INPUT = "w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300";
const LABEL = "block text-xs font-semibold text-gray-500 mb-1";
const BTN_PRIMARY = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition disabled:opacity-50";
const BTN_SECONDARY = "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition disabled:opacity-50";
const BTN_DANGER = "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 text-sm font-semibold transition disabled:opacity-50";

const errorMessage = (e, fallback) => e?.response?.data?.message || fallback;

function Pill({ className, children }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      {children}
    </span>
  );
}

function VerifiedBadge({ user }) {
  if (user?.verificationStatus !== "aprobado") return null;
  return <BadgeCheck size={15} className="text-blue-500 flex-shrink-0" title="Identidad verificada" />;
}

function Avatar({ user, size = "w-10 h-10" }) {
  return (
    <div className={`${size} rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0`}>
      {user?.avatar
        ? <img src={getImageUrl(user.avatar)} alt={user.name} className="w-full h-full object-cover" />
        : <span className="text-sm font-bold text-gray-400">{user?.name?.[0]?.toUpperCase() ?? "?"}</span>}
    </div>
  );
}

function ComposeModal({ type, ryder, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const isWA = type === "whatsapp";
  const userId = ryder.user?.id;

  const handleSend = async () => {
    if (!message.trim()) return toast.error("El mensaje no puede estar vacío");
    if (!isWA && !subject.trim()) return toast.error("El asunto no puede estar vacío");
    setSending(true);
    try {
      const res = await axios.post(
        isWA ? `/users/${userId}/send-whatsapp` : `/users/${userId}/send-email`,
        isWA ? { message } : { subject, message },
      );
      toast.success(res.data?.message || "Mensaje enviado");
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Error enviando mensaje"));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10010] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-800">{isWA ? "Enviar WhatsApp" : "Enviar correo"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-500">
            Para: <span className="font-semibold text-gray-800">{ryder.user?.name}</span>{" "}
            <span className="text-gray-400">({isWA ? (ryder.user?.phone || "sin teléfono") : ryder.user?.email})</span>
          </p>
          {!isWA && (
            <div>
              <label className={LABEL}>Asunto</label>
              <input className={INPUT} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          )}
          <div>
            <label className={LABEL}>Mensaje</label>
            <textarea className={INPUT} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <button onClick={handleSend} disabled={sending} className={`${BTN_PRIMARY} w-full`}>
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RyderFormModal({ mode, ryder, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [saving, setSaving] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState(() => ({
    name: ryder?.user?.name ?? "",
    email: ryder?.user?.email ?? "",
    phone: ryder?.user?.phone ?? "",
    phoneCountryCode: ryder?.user?.phoneCountryCode ?? "+58",
    documentType: ryder?.user?.documentType ?? "CC",
    documentNumber: ryder?.user?.documentNumber ?? "",
    vehicleType: ryder?.vehicleType ?? "moto",
    vehiclePlate: ryder?.vehiclePlate ?? "",
    vehicleBrand: ryder?.vehicleBrand ?? "",
    vehicleModel: ryder?.vehicleModel ?? "",
    vehicleCapacity: ryder?.vehicleCapacity ?? "",
    approvalStatus: ryder?.approvalStatus ?? "pendiente",
  }));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const searchUsers = async () => {
    if (!userQuery.trim()) return;
    try {
      const res = await axios.get("/users", { params: { search: userQuery.trim(), limit: 8 } });
      setUserResults(res.data?.data?.data ?? []);
    } catch { toast.error("Error buscando usuarios"); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const { approvalStatus, ...fields } = form;
        await axios.patch(`/ryders/${ryder.id}/admin`, fields);
        if (approvalStatus !== ryder.approvalStatus)
          await axios.patch(`/ryders/${ryder.id}/approval`, { approvalStatus });
        toast.success("Domiciliario actualizado");
      } else {
        if (!selectedUser) return toast.error("Selecciona un usuario");
        await axios.post("/ryders/admin", {
          userId: selectedUser.id,
          approvalStatus: form.approvalStatus,
          vehicleType: form.vehicleType,
          vehiclePlate: form.vehiclePlate,
          vehicleBrand: form.vehicleBrand,
          vehicleModel: form.vehicleModel,
          vehicleCapacity: form.vehicleCapacity,
        });
        toast.success("Domiciliario creado");
      }
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "Error guardando domiciliario"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10010] p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-gray-800">{isEdit ? "Editar domiciliario" : "Nuevo domiciliario"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-4 overflow-y-auto space-y-5">
          {!isEdit && (
            <section className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">1. Usuario</p>
              <p className="text-xs text-gray-500">El usuario debe tener cuenta. Se le asignará el perfil de domiciliario.</p>
              <div className="flex gap-2">
                <input className={INPUT} placeholder="Buscar por nombre, correo o teléfono"
                  value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && searchUsers()} />
                <button onClick={searchUsers} className={BTN_SECONDARY}><Search size={14} /></button>
              </div>
              {selectedUser && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                  <span className="font-semibold text-gray-800">{selectedUser.name}</span>
                  <span className="text-gray-500">{selectedUser.email}</span>
                </div>
              )}
              {userResults.length > 0 && (
                <ul className="border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                  {userResults.map((u) => (
                    <li key={u.id}>
                      <button onClick={() => { setSelectedUser(u); setUserResults([]); }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                        <span className="font-semibold text-gray-800">{u.name}</span>{" "}
                        <span className="text-gray-400">{u.email || u.phone}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {isEdit && (
            <section className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">Datos personales</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={LABEL}>Nombre</label><input className={INPUT} value={form.name} onChange={set("name")} /></div>
                <div><label className={LABEL}>Correo</label><input className={INPUT} type="email" value={form.email} onChange={set("email")} /></div>
                <div>
                  <label className={LABEL}>Teléfono</label>
                  <div className="flex gap-2">
                    <select className="px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={form.phoneCountryCode} onChange={set("phoneCountryCode")}>
                      {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                    </select>
                    <input className={INPUT} value={form.phone} onChange={set("phone")} />
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Tipo de documento</label>
                  <select className={INPUT} value={form.documentType} onChange={set("documentType")}>
                    {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2"><label className={LABEL}>Número de documento</label><input className={INPUT} value={form.documentNumber} onChange={set("documentNumber")} /></div>
              </div>
            </section>
          )}

          <section className="space-y-3">
            <p className="text-sm font-semibold text-gray-700">{isEdit ? "Vehículo" : "2. Vehículo"}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Tipo</label>
                <select className={INPUT} value={form.vehicleType} onChange={set("vehicleType")}>
                  {VEHICLE_OPTIONS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div><label className={LABEL}>Placa</label><input className={INPUT} value={form.vehiclePlate} onChange={set("vehiclePlate")} /></div>
              <div><label className={LABEL}>Marca</label><input className={INPUT} value={form.vehicleBrand} onChange={set("vehicleBrand")} /></div>
              <div><label className={LABEL}>Modelo</label><input className={INPUT} value={form.vehicleModel} onChange={set("vehicleModel")} /></div>
              <div className="sm:col-span-2"><label className={LABEL}>Capacidad de carga</label><input className={INPUT} value={form.vehicleCapacity} onChange={set("vehicleCapacity")} /></div>
            </div>
          </section>

          <section className="space-y-2">
            <label className={LABEL}>Estado de aprobación</label>
            <select className={INPUT} value={form.approvalStatus} onChange={set("approvalStatus")}>
              <option value="pendiente">Pendiente</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
            </select>
          </section>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className={BTN_SECONDARY}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className={BTN_PRIMARY}>
            {saving && <RefreshCw size={14} className="animate-spin" />}
            {isEdit ? "Guardar cambios" : "Crear domiciliario"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ mode, saving, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [canResubmit, setCanResubmit] = useState(true);
  const isDeactivate = mode === "deactivate";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10010] p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4 shadow-2xl">
        <h3 className="font-bold text-gray-800">{isDeactivate ? "Desactivar domiciliario" : "Rechazar solicitud"}</h3>
        <div>
          <label className={LABEL}>Motivo (obligatorio, se le mostrará al usuario)</label>
          <textarea className={INPUT} rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ej. La foto del documento no es legible" />
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="mt-0.5" checked={canResubmit} onChange={(e) => setCanResubmit(e.target.checked)} />
          Permitir que el usuario vuelva a enviar la solicitud
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={BTN_SECONDARY}>Cancelar</button>
          <button disabled={!reason.trim() || saving}
            onClick={() => onConfirm({ rejectionReason: reason.trim(), canResubmit })}
            className={BTN_DANGER}>
            {saving && <RefreshCw size={14} className="animate-spin" />}
            {isDeactivate ? "Desactivar" : "Rechazar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RyderDetail({ ryderId, onClose, onChanged }) {
  const [ryder, setRyder] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [compose, setCompose] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [formMode, setFormMode] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [rejectMode, setRejectMode] = useState(null);

  const sendRejection = async ({ rejectionReason, canResubmit }) => {
    const label = rejectMode === "deactivate" ? "Cuenta desactivada" : "Solicitud rechazada";
    await runAction("reject", () => axios.patch(`/ryders/${ryderId}/approval`, {
      approvalStatus: "rechazado", rejectionReason, canResubmit,
    }), label);
    setRejectMode(null);
  };

  const load = useCallback(async () => {
    try {
      const [rRes, sRes, hRes] = await Promise.all([
        axios.get(`/ryders/${ryderId}`),
        axios.get(`/ryders/${ryderId}/stats`).catch(() => ({ data: {} })),
        axios.get(`/ryders/${ryderId}/history`).catch(() => ({ data: {} })),
      ]);
      setRyder(rRes.data?.data?.ryder ?? null);
      setStats(sRes.data?.data ?? null);
      setHistory(hRes.data?.data?.data ?? []);
    } catch {
      toast.error("Error cargando el domiciliario");
    } finally { setLoading(false); }
  }, [ryderId]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, "Error en la acción"));
    } finally { setBusy(null); }
  };

  const handleDelete = async () => {
    setBusy("delete");
    try {
      await axios.delete(`/ryders/${ryderId}`);
      toast.success("Domiciliario eliminado");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "No se pudo eliminar"));
      setConfirmDelete(false);
    } finally { setBusy(null); }
  };

  const lat = Number(ryder?.lat ?? 0);
  const lng = Number(ryder?.lng ?? 0);
  const hasLocation = lat !== 0 || lng !== 0;
  const VehicleIcon = VEHICLE_ICON[ryder?.vehicleType] ?? Truck;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[10000]" onClick={onClose} />
      <aside className="fixed top-0 right-0 bottom-0 w-full sm:w-[520px] bg-gray-50 z-[10001] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-bold text-gray-800">Perfil del domiciliario</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex justify-center p-16"><RefreshCw className="animate-spin text-gray-300" size={28} /></div>
        ) : !ryder ? (
          <p className="p-16 text-center text-gray-400 text-sm">Domiciliario no encontrado</p>
        ) : (
          <div className="p-5 space-y-5">
            {/* Identidad */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 flex gap-4">
              <Avatar user={{ name: ryder.user?.name, avatar: ryder.avatar }} size="w-20 h-20" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h3 className="font-bold text-gray-800 text-lg truncate">{ryder.user?.name}</h3>
                  <VerifiedBadge user={ryder.user} />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Pill className={APPROVAL_STYLE[ryder.approvalStatus] ?? APPROVAL_STYLE.pendiente}>
                    {APPROVAL_LABEL[ryder.approvalStatus] ?? ryder.approvalStatus}
                  </Pill>
                  <Pill className={STATUS_STYLE[ryder.status] ?? STATUS_STYLE.offline}>
                    {STATUS_LABEL[ryder.status] ?? ryder.status}
                  </Pill>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                  {ryder.user?.email && <span className="flex items-center gap-1"><Mail size={13} />{ryder.user.email}</span>}
                  {ryder.user?.phone && <span className="flex items-center gap-1"><Phone size={13} />{ryder.user.phoneCountryCode} {ryder.user.phone}</span>}
                </div>
                {ryder.company?.name ? (
                  <p className="flex items-center gap-1.5 mt-2 text-sm text-gray-600">
                    <Building2 size={13} className="text-gray-400 shrink-0" /> Domiciliario propio de <span className="font-semibold">{ryder.company.name}</span>
                  </p>
                ) : (
                  <p className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                    <Building2 size={13} className="shrink-0" /> Domiciliario de plataforma (sin tienda propia)
                  </p>
                )}
              </div>
            </div>

            {ryder.approvalStatus === "rechazado" && ryder.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 space-y-2">
                <p className="text-sm font-semibold text-red-700">Motivo del rechazo</p>
                <p className="text-sm text-red-700 whitespace-pre-line">{ryder.rejectionReason}</p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-red-600">
                    {ryder.canResubmit ? "El usuario puede volver a enviar la solicitud." : "El usuario no puede volver a enviar la solicitud."}
                  </span>
                  {!ryder.canResubmit && (
                    <button disabled={!!busy}
                      onClick={() => runAction("allow", () => axios.patch(`/ryders/${ryder.id}/approval`, {
                        approvalStatus: "rechazado", rejectionReason: ryder.rejectionReason, canResubmit: true,
                      }), "El usuario podrá volver a enviar la solicitud")}
                      className={BTN_SECONDARY}>
                      Permitir reenvío
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Métricas */}
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: "Entregas", value: ryder.totalDeliveries ?? 0 },
                { label: "Rating", value: Number(ryder.rating ?? 0).toFixed(1) },
                { label: "Reseñas", value: stats?.rating?.count ?? 0 },
                { label: "Ingresos", value: `$${Number(stats?.earnings?.total ?? 0).toLocaleString("es-CO")}` },
              ].map((m) => (
                <div key={m.label} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
                  <div className="text-base font-bold text-gray-800">{m.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>

            {/* Acciones de cuenta */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Acciones</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setFormMode("edit")} className={BTN_SECONDARY}><Pencil size={14} /> Editar</button>
                {ryder.approvalStatus !== "aprobado" && (
                  <button disabled={!!busy}
                    onClick={() => runAction("approve", () => axios.patch(`/ryders/${ryder.id}/approval`, { approvalStatus: "aprobado" }), "Domiciliario aprobado")}
                    className={BTN_PRIMARY}>
                    {busy === "approve" ? <RefreshCw size={14} className="animate-spin" /> : <UserCheck size={14} />} Aprobar
                  </button>
                )}
                {ryder.approvalStatus === "aprobado" ? (
                  <button disabled={!!busy} onClick={() => setRejectMode("deactivate")} className={BTN_DANGER}>
                    <ShieldOff size={14} /> Desactivar
                  </button>
                ) : (
                  <button disabled={!!busy} onClick={() => setRejectMode("reject")} className={BTN_DANGER}>
                    <UserX size={14} /> Rechazar
                  </button>
                )}
                <button onClick={() => setShowQR(true)} className={BTN_SECONDARY}><QrCode size={14} /> QR</button>
                <button onClick={() => setConfirmDelete(true)} className={BTN_DANGER}><Trash2 size={14} /> Eliminar</button>
              </div>
            </div>

            {/* Vehículo */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Vehículo</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 col-span-2 text-gray-800 capitalize"><VehicleIcon size={16} className="text-gray-400" /> {ryder.vehicleType}</div>
                {ryder.vehiclePlate && <div><span className="text-gray-400 text-xs block">Placa</span>{ryder.vehiclePlate}</div>}
                {ryder.vehicleBrand && <div><span className="text-gray-400 text-xs block">Marca</span>{ryder.vehicleBrand}</div>}
                {ryder.vehicleModel && <div><span className="text-gray-400 text-xs block">Modelo</span>{ryder.vehicleModel}</div>}
                {ryder.vehicleCapacity && <div><span className="text-gray-400 text-xs block">Capacidad</span>{ryder.vehicleCapacity}</div>}
              </div>
            </div>

            {/* Documento y verificación */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-700">Documento de identidad</p>
                {ryder.user?.verificationStatus && (
                  <Pill className={DOC_STYLE[ryder.user.verificationStatus] ?? DOC_STYLE.pendiente}>
                    {DOC_LABEL[ryder.user.verificationStatus] ?? ryder.user.verificationStatus}
                  </Pill>
                )}
              </div>
              <div className="text-sm text-gray-600">
                {ryder.user?.documentType} {ryder.user?.documentNumber ?? <span className="text-gray-400">Sin número</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {ryder.user?.documentPhoto && (
                  <a href={getImageUrl(ryder.user.documentPhoto)} target="_blank" rel="noopener noreferrer" className={BTN_SECONDARY}>
                    <IdCard size={14} /> Ver documento
                  </a>
                )}
                {ryder.user?.id && (
                  <button onClick={() => setVerifyOpen(true)} className={BTN_PRIMARY}>
                    <ShieldCheck size={14} /> Validar documento
                  </button>
                )}
              </div>
            </div>

            {/* Verificación de contacto y mensajes */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700">Contacto</p>
              <div className="flex flex-wrap gap-2">
                <button disabled={!!ryder.user?.emailVerified || busy === "resend-email"}
                  onClick={() => runAction("resend-email", () => axios.post(`/users/${ryder.user.id}/resend-verification-email`), "Email de verificación enviado")}
                  className={BTN_SECONDARY}>
                  <Mail size={14} /> {ryder.user?.emailVerified ? "Email verificado" : "Verificar email"}
                </button>
                <button disabled={!ryder.user?.phone || busy === "resend-wa"}
                  onClick={() => runAction("resend-wa", () => axios.post(`/users/${ryder.user.id}/resend-whatsapp`), "Código WhatsApp enviado")}
                  className={BTN_SECONDARY}>
                  <MessageSquare size={14} /> Verificar WhatsApp
                </button>
                <button disabled={!ryder.user?.phone} onClick={() => setCompose("whatsapp")} className={BTN_SECONDARY}>
                  <MessageSquare size={14} /> Mensaje WhatsApp
                </button>
                <button onClick={() => setCompose("email")} className={BTN_SECONDARY}>
                  <Mail size={14} /> Mensaje email
                </button>
              </div>
            </div>

            {/* Ubicación */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 border-b text-sm font-semibold text-gray-700 flex items-center gap-2">
                <MapPin size={14} className="text-gray-400" /> Última ubicación
              </div>
              {hasLocation ? (
                <div className="h-48">
                  <MapContainer center={[lat, lng]} zoom={15} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />
                    <CircleMarker center={[lat, lng]} radius={9} pathOptions={{ color: "#fff", fillColor: "#3B82F6", fillOpacity: 1, weight: 3 }}>
                      <Popup>{ryder.user?.name}</Popup>
                    </CircleMarker>
                  </MapContainer>
                </div>
              ) : (
                <p className="p-6 text-center text-sm text-gray-400">Sin ubicación disponible</p>
              )}
            </div>

            {/* Historial */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Historial de entregas</p>
              {history.length === 0 ? (
                <p className="text-sm text-gray-400 flex items-center gap-2"><Package size={14} /> Sin entregas registradas</p>
              ) : (
                <ul className="divide-y">
                  {history.slice(0, 20).map((order) => (
                    <li key={order.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{order.company?.name ?? "Pedido"}</p>
                        <p className="text-xs text-gray-400">{order.createdAt ? dayjs(order.createdAt).format("DD MMM YYYY") : "—"}</p>
                      </div>
                      <Pill className={ORDER_STATUS_STYLE[order.status] ?? "bg-gray-100 text-gray-600"}>{order.status}</Pill>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Reseñas de clientes — el backend ya las calcula (GET /ryders/:id/stats)
                pero antes nunca se mostraban en el perfil. */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Reseñas de clientes</p>
              {(stats?.reviews ?? []).length === 0 ? (
                <p className="text-sm text-gray-400 flex items-center gap-2"><Star size={14} /> Sin reseñas todavía</p>
              ) : (
                <ul className="divide-y">
                  {stats.reviews.map((r, i) => (
                    <li key={i} className="py-2.5 space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-semibold text-gray-800 truncate">{r.userName ?? "Cliente"}</span>
                        <span className="flex items-center gap-0.5 text-amber-500 shrink-0">
                          <Star size={12} className="fill-amber-500" /> {Number(r.score ?? 0).toFixed(1)}
                        </span>
                      </div>
                      {r.comment && <p className="text-sm text-gray-600">{r.comment}</p>}
                      {r.ratedAt && <p className="text-xs text-gray-400">{dayjs(r.ratedAt).format("DD MMM YYYY")}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </aside>

      {rejectMode && (
        <RejectModal mode={rejectMode} saving={busy === "reject"}
          onConfirm={sendRejection} onClose={() => setRejectMode(null)} />
      )}

      {verifyOpen && ryder?.user && (
        <UserVerificationModal user={ryder.user} onClose={() => setVerifyOpen(false)}
          onUpdated={() => { load(); onChanged(); }} />
      )}

      {compose && ryder && (
        <ComposeModal type={compose} ryder={ryder} onClose={() => setCompose(null)} />
      )}

      {formMode && ryder && (
        <RyderFormModal mode="edit" ryder={ryder} onClose={() => setFormMode(null)}
          onSaved={() => { setFormMode(null); load(); onChanged(); }} />
      )}

      {showQR && ryder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10010] p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <p className="font-bold text-gray-800 mb-4">{ryder.user?.name}</p>
            <QRCodeSVG value={ryder.id} size={180} />
            <p className="text-xs text-gray-400 mt-3 break-all">ID: {ryder.id}</p>
            <button onClick={() => setShowQR(false)} className={`${BTN_SECONDARY} mt-4`}>Cerrar</button>
          </div>
        </div>
      )}

      {confirmDelete && ryder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10010] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
            <h3 className="font-bold text-gray-800">¿Eliminar domiciliario?</h3>
            <p className="text-sm text-gray-500">
              Se eliminará el perfil de domiciliario de {ryder.user?.name}. La cuenta del usuario se conserva.
              Si tiene pedidos o viajes, la eliminación no se permite.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} className={BTN_SECONDARY}>Cancelar</button>
              <button onClick={handleDelete} disabled={busy === "delete"} className={BTN_DANGER}>
                {busy === "delete" && <RefreshCw size={14} className="animate-spin" />} Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminRyders() {
  const [ryders, setRyders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [editing, setEditing] = useState(null);

  const fetchRyders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/ryders", { params: { limit: 200 } });
      setRyders(res.data?.data?.data ?? []);
      setTotal(res.data?.data?.total ?? 0);
    } catch {
      toast.error("Error al cargar domiciliarios");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRyders(); }, [fetchRyders]);

  const q = search.trim().toLowerCase();
  const filtered = ryders.filter((r) => {
    const matchFilter = filter === "all" || r.approvalStatus === filter;
    const matchSearch = !q
      || r.user?.name?.toLowerCase().includes(q)
      || r.user?.email?.toLowerCase().includes(q)
      || r.user?.phone?.includes(q);
    return matchFilter && matchSearch;
  });

  const counts = {
    all: ryders.length,
    pendiente: ryders.filter((r) => r.approvalStatus === "pendiente").length,
    aprobado: ryders.filter((r) => r.approvalStatus === "aprobado").length,
    rechazado: ryders.filter((r) => r.approvalStatus === "rechazado").length,
  };

  const FILTERS = [
    ["all", "Todos"],
    ["pendiente", "Pendientes"],
    ["aprobado", "Aprobados"],
    ["rechazado", "Rechazados"],
  ];

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-0 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck size={24} className="text-blue-500" /> Domiciliarios
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">{total} domiciliarios registrados</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchRyders} className={BTN_SECONDARY}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
          </button>
          <button onClick={() => { setEditing(null); setFormMode("create"); }} className={BTN_PRIMARY}>
            <Plus size={14} /> Nuevo domiciliario
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: counts.all, cls: "bg-gray-50 border-gray-200 text-gray-800" },
          { label: "Pendientes", value: counts.pendiente, cls: "bg-amber-50 border-amber-200 text-amber-700" },
          { label: "Aprobados", value: counts.aprobado, cls: "bg-green-50 border-green-200 text-green-700" },
          { label: "Rechazados", value: counts.rechazado, cls: "bg-red-50 border-red-200 text-red-600" },
        ].map((s) => (
          <div key={s.label} className={`${s.cls} border rounded-xl p-3 text-center`}>
            <div className="text-2xl font-bold">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, correo o teléfono…"
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 shadow-sm" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
              className={`px-3 py-2 rounded-xl text-sm font-semibold border transition ${filter === key ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Domiciliario</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Contacto</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Vehículo</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aprobación</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Documento</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Entregas</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && ryders.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center"><RefreshCw className="animate-spin mx-auto text-gray-300" size={24} /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center text-gray-400 text-sm">No hay domiciliarios que mostrar</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50/50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar user={{ name: r.user?.name, avatar: r.avatar }} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 font-semibold text-gray-800 truncate max-w-[180px]">
                          {r.user?.name}<VerifiedBadge user={r.user} />
                        </div>
                        <div className="text-xs text-gray-400 truncate max-w-[180px]">{r.user?.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-600 font-mono">
                    {r.user?.phone ? `${r.user.phoneCountryCode ?? ""} ${r.user.phone}` : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600 capitalize">
                    {r.vehicleType}{r.vehiclePlate && <span className="text-gray-400 normal-case"> · {r.vehiclePlate}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Pill className={APPROVAL_STYLE[r.approvalStatus] ?? APPROVAL_STYLE.pendiente}>
                      {APPROVAL_LABEL[r.approvalStatus] ?? r.approvalStatus}
                    </Pill>
                    <div className="mt-1">
                      <Pill className={STATUS_STYLE[r.status] ?? STATUS_STYLE.offline}>{STATUS_LABEL[r.status] ?? r.status}</Pill>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {r.user?.documentPhoto ? (
                      <Pill className={DOC_STYLE[r.user.verificationStatus] ?? DOC_STYLE.pendiente}>
                        {DOC_LABEL[r.user.verificationStatus] ?? "En revisión"}
                      </Pill>
                    ) : (
                      <span className="text-xs text-gray-300">Sin documento</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                    {r.totalDeliveries ?? 0}
                    <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-amber-600">
                      <Star size={11} className="fill-amber-400 stroke-amber-400" /> {Number(r.rating ?? 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button onClick={() => setSelectedId(r.id)}
                        className="px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-semibold border border-blue-200 transition inline-flex items-center gap-1">
                        <Eye size={13} /> Ver
                      </button>
                      <button onClick={() => { setEditing(r); setFormMode("edit"); }}
                        className="px-2.5 py-1.5 rounded-lg bg-white hover:bg-gray-50 text-gray-600 text-xs font-semibold border border-gray-200 transition inline-flex items-center gap-1">
                        <Pencil size={13} /> Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedId && (
        <RyderDetail key={selectedId} ryderId={selectedId} onClose={() => setSelectedId(null)} onChanged={fetchRyders} />
      )}

      {formMode === "create" && (
        <RyderFormModal mode="create" onClose={() => setFormMode(null)}
          onSaved={() => { setFormMode(null); fetchRyders(); }} />
      )}

      {formMode === "edit" && editing && (
        <RyderFormModal mode="edit" ryder={editing} onClose={() => setFormMode(null)}
          onSaved={() => { setFormMode(null); fetchRyders(); }} />
      )}
    </div>
  );
}
